import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormHelperText,
  Autocomplete,
  Button,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTimeValidationError } from "@mui/x-date-pickers/internals/hooks/validation/useDateTimeValidation";
import {
  CarrierFilterFieldProps,
  ClearFiltersActionProps,
  DateFilterFieldsProps,
  EditStatesActionProps,
  IFiltersHeaderViewProps,
  IdFilterFieldProps,
  LoaderOverlayProps,
  MerchantFilterFieldProps,
  SearchFieldProps,
  StatusFilterFieldProps,
  ISlaAlertLevelValue,
} from "../../interfaces/order";
import {
  LOCALE,
  DATE_FORMAT,
  ERROR_FROM_GREATER_THAN_TO,
  ERROR_TO_GREATER_THAN_FROM,
  CARRIER_LABEL,
  MERCHANT,
  ECOMMERCE,
  SLA_ALERT_LEVEL_CONFIG,
} from "../../utils/constants";
import { formatSlaThreshold } from "../../utils/orders";
import SearchInput from "../SearchInput";
import FiltersHeaderSkeleton from "./FiltersHeaderSkeleton";
import StatusFilterSelect from "./StatusFilterSelect";

const DateFilterFields: React.FC<DateFilterFieldsProps> = ({
  showDateFilters,
  picker,
  onFromDateChange,
  onFromDateError,
  onToDateChange,
  onToDateError,
  errors,
}) => {
  if (!showDateFilters) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={LOCALE}>
      <Grid item xs={12} md={2}>
        <DatePicker
          label="Desde"
          value={picker.fromDate}
          disableFuture
          inputFormat={DATE_FORMAT}
          onChange={(value) => {
            if (!value || value.isValid) onFromDateChange(value);
          }}
          onError={(reason: DateTimeValidationError) => onFromDateError(reason)}
          renderInput={(params) => (
            <TextField fullWidth size="small" {...params} />
          )}
        />
        <FormHelperText className="color-error" key="error-message">
          {errors.from && "Fecha inválida"}
          {errors.invalidFrom && ERROR_FROM_GREATER_THAN_TO}
        </FormHelperText>
      </Grid>
      <Grid item xs={12} md={2}>
        <DatePicker
          label="Hasta"
          value={picker.toDate}
          disableFuture
          inputFormat={DATE_FORMAT}
          onChange={(value) => {
            if (!value || value.isValid) onToDateChange(value);
          }}
          onError={(reason: DateTimeValidationError) => onToDateError(reason)}
          renderInput={(params) => (
            <TextField fullWidth size="small" {...params} />
          )}
        />
        <FormHelperText className="color-error" key="error-message">
          {errors.to && "Fecha inválida"}
          {errors.invalidTo && ERROR_TO_GREATER_THAN_FROM}
        </FormHelperText>
      </Grid>
    </LocalizationProvider>
  );
};

const MerchantFilterField: React.FC<MerchantFilterFieldProps> = ({
  showMerchantFilter,
  showCarrierFilter,
  merchants,
  onMerchantChange,
}) => {
  if (!showMerchantFilter) return null;
  return (
    <Grid item xs={12} md={6} lg={showCarrierFilter ? 2.5 : 3.75}>
      <Autocomplete
        id="merchants"
        options={merchants}
        noOptionsText="No se encontró el merchant"
        onChange={(_, value: { id: number } | null) =>
          onMerchantChange(value?.id)
        }
        renderInput={(params) => (
          <TextField {...params} fullWidth size="small" label="Merchant" />
        )}
      />
    </Grid>
  );
};

const CarrierFilterField: React.FC<CarrierFilterFieldProps> = ({
  v2Enabled,
  showCarrierFilter,
  showMerchantFilter,
  carrierOptions,
  selectedCarriers,
  onCarriersChange,
}) => {
  if (!(v2Enabled && showCarrierFilter)) return null;
  return (
    <Grid item xs={12} md={6} lg={showMerchantFilter ? 2.5 : 3.75}>
      <Autocomplete
        multiple
        id="carrier-filter"
        options={carrierOptions}
        value={selectedCarriers}
        onChange={(_, values) => onCarriersChange(values as string[])}
        getOptionLabel={(option) => CARRIER_LABEL[option] || option}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size="small"
            label="Operador logístico"
          />
        )}
      />
    </Grid>
  );
};

const SearchField: React.FC<SearchFieldProps> = ({
  showCarrierFilter,
  showMerchantFilter,
  searchValue,
  handleSubmit,
  handleChange,
  handleClose,
  activeSearch,
}) => (
  <Grid
    item
    xs={12}
    md={6}
    lg={showCarrierFilter && showMerchantFilter ? 3 : 4.25}
    className="search-min-width"
  >
    <SearchInput
      {...{
        searchValue,
        handleSubmit,
        handleChange,
        handleClose,
        activeSearch,
      }}
      placeholder="Buscar por n° de guía, dni o id externo"
    />
  </Grid>
);

const IdFilterField: React.FC<IdFilterFieldProps> = ({
  v2Enabled,
  showIdFilter,
  idOptions,
  selectedIds,
  onIdsChange,
}) => {
  if (!(v2Enabled && showIdFilter)) return null;
  return (
    <Grid item xs={12} md={6} lg={2}>
      <Autocomplete
        multiple
        freeSolo
        id="order-id-filter"
        options={idOptions}
        value={selectedIds}
        onChange={(_, values) => onIdsChange(values as string[])}
        renderInput={(params) => (
          <TextField {...params} fullWidth size="small" label="IDs de orden" />
        )}
      />
    </Grid>
  );
};

type SlaAlertLevelFilterFieldProps = {
  v2Enabled: boolean;
  showSlaAlertLevelFilter: boolean;
  slaAlertLevelOptions: (string | ISlaAlertLevelValue)[];
  selectedSlaAlertLevels: string[];
  onSlaAlertLevelsChange: (_values: string[]) => void;
};

const SlaAlertLevelFilterField: React.FC<SlaAlertLevelFilterFieldProps> = ({
  v2Enabled,
  showSlaAlertLevelFilter,
  slaAlertLevelOptions,
  selectedSlaAlertLevels,
  onSlaAlertLevelsChange,
}) => {
  if (!(v2Enabled && showSlaAlertLevelFilter)) return null;

  const getOptionValue = (option: string | ISlaAlertLevelValue): string =>
    typeof option === "string" ? option : option.value;

  const getOptionLabel = (option: string | ISlaAlertLevelValue): string => {
    const value = getOptionValue(option);
    const baseLabel = SLA_ALERT_LEVEL_CONFIG[value]?.label || value;

    if (typeof option === "object" && option.threshold) {
      const thresholdLabel = formatSlaThreshold(
        option.threshold.amount,
        option.threshold.unit
      );
      return baseLabel + thresholdLabel;
    }

    return baseLabel;
  };

  const findOptionByValue = (value: string): string | ISlaAlertLevelValue => {
    const found = slaAlertLevelOptions.find(
      (opt) => getOptionValue(opt) === value
    );
    return found || value;
  };

  return (
    <Grid item xs={12} md={6} lg={2}>
      <Autocomplete
        multiple
        id="sla-alert-level-filter"
        options={slaAlertLevelOptions}
        value={selectedSlaAlertLevels.map(findOptionByValue)}
        onChange={(_, values) => {
          const stringValues = values.map(getOptionValue);
          onSlaAlertLevelsChange(stringValues);
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
        renderInput={(params) => (
          <TextField {...params} fullWidth size="small" label="Nivel de alerta SLA" />
        )}
      />
    </Grid>
  );
};

const StatusFilterField: React.FC<StatusFilterFieldProps> = ({
  showStatusFilter,
  v2Enabled,
  statusList,
  selectedStatus,
  setSelectedStatus,
  selectedStatuses,
  setSelectedStatuses,
  handleChangeFilter,
}) => {
  if (!showStatusFilter) return null;
  return (
    <StatusFilterSelect
      v2Enabled={v2Enabled}
      statusList={statusList}
      selectedStatus={selectedStatus}
      setSelectedStatus={(value) => setSelectedStatus(value)}
      selectedStatuses={selectedStatuses}
      setSelectedStatuses={(value) => setSelectedStatuses(value)}
      handleChangeFilter={handleChangeFilter}
    />
  );
};

const EditStatesAction: React.FC<EditStatesActionProps> = ({
  entity,
  selectedOrdersCount,
  setOpenEditModal,
  disableEdit,
}) => {
  const canEdit =
    (entity === MERCHANT || entity === ECOMMERCE) && selectedOrdersCount > 0 && !!setOpenEditModal;
  if (!canEdit) return null;
  return (
    <Grid item xs={6} md={2}>
      <Button
        variant="contained"
        fullWidth
        onClick={() => setOpenEditModal && setOpenEditModal(true)}
        disabled={disableEdit}
      >
        Editar estados
      </Button>
    </Grid>
  );
};

const ClearFiltersAction: React.FC<ClearFiltersActionProps> = ({
  handleClearFilters,
}) => (
  <Grid item xs={6} md={2}>
    <Button
      variant="contained"
      color="primary"
      fullWidth
      onClick={handleClearFilters}
    >
      Limpiar filtros
    </Button>
  </Grid>
);

const LoaderOverlay: React.FC<LoaderOverlayProps> = ({
  loaderPage,
  isOwnFleet,
}) => {
  if (!loaderPage) return null;
  return (
    <Box position="relative" zIndex={2}>
      <FiltersHeaderSkeleton isOwnFleet={isOwnFleet} />
    </Box>
  );
};

const FiltersHeaderView = ({ viewProps }: IFiltersHeaderViewProps) => (
  <Box position="relative">
    <LoaderOverlay
      loaderPage={viewProps.loaderPage}
      isOwnFleet={viewProps.isOwnFleet}
    />
    <Grid
      container
      className="m-t-10"
      display={"flex"}
      spacing={2}
      sx={
        viewProps.loaderPage
          ? {
            visibility: "hidden",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
          }
          : {}
      }
    >
      <DateFilterFields
        showDateFilters={viewProps.showDateFilters}
        picker={viewProps.picker}
        onFromDateChange={viewProps.onFromDateChange}
        onFromDateError={viewProps.onFromDateError}
        onToDateChange={viewProps.onToDateChange}
        onToDateError={viewProps.onToDateError}
        errors={viewProps.errors}
      />
      <MerchantFilterField
        showMerchantFilter={viewProps.showMerchantFilter}
        showCarrierFilter={viewProps.showCarrierFilter}
        merchants={viewProps.merchants}
        onMerchantChange={viewProps.onMerchantChange}
      />
      <CarrierFilterField
        v2Enabled={viewProps.v2Enabled}
        showCarrierFilter={viewProps.showCarrierFilter}
        showMerchantFilter={viewProps.showMerchantFilter}
        carrierOptions={viewProps.carrierOptions}
        selectedCarriers={viewProps.selectedCarriers}
        onCarriersChange={viewProps.onCarriersChange}
      />
      <SearchField
        showCarrierFilter={viewProps.showCarrierFilter}
        showMerchantFilter={viewProps.showMerchantFilter}
        searchValue={viewProps.searchValue}
        handleSubmit={viewProps.handleSubmit}
        handleChange={viewProps.handleChange}
        handleClose={viewProps.handleClose}
        activeSearch={viewProps.activeSearch}
      />
      <IdFilterField
        v2Enabled={viewProps.v2Enabled}
        showIdFilter={viewProps.showIdFilter}
        idOptions={viewProps.idOptions}
        selectedIds={viewProps.selectedIds}
        onIdsChange={viewProps.onIdsChange}
      />
      <StatusFilterField
        showStatusFilter={viewProps.showStatusFilter}
        v2Enabled={viewProps.v2Enabled}
        statusList={viewProps.statusList}
        selectedStatus={viewProps.selectedStatus}
        setSelectedStatus={viewProps.setSelectedStatus}
        selectedStatuses={viewProps.selectedStatuses}
        setSelectedStatuses={viewProps.setSelectedStatuses}
        handleChangeFilter={viewProps.handleChangeFilter}
      />
      <SlaAlertLevelFilterField
        v2Enabled={viewProps.v2Enabled}
        showSlaAlertLevelFilter={viewProps.showSlaAlertLevelFilter}
        slaAlertLevelOptions={viewProps.slaAlertLevelOptions}
        selectedSlaAlertLevels={viewProps.selectedSlaAlertLevels}
        onSlaAlertLevelsChange={viewProps.onSlaAlertLevelsChange}
      />
      <EditStatesAction
        entity={viewProps.entity}
        selectedOrdersCount={viewProps.selectedOrdersCount}
        setOpenEditModal={viewProps.setOpenEditModal}
        disableEdit={viewProps.disableEdit}
      />
      <ClearFiltersAction handleClearFilters={viewProps.handleClearFilters} />
    </Grid>
  </Box>
);

export default FiltersHeaderView;
